import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { LINK_TITLE_MAX, LINK_URL_MAX, linkLabel, type ProjectLink } from '@jisy/shared';
import { ApiRequestError } from '../api/client.js';
import { linksQuery, useLinkCreate, useLinkDelete, useLinkSave } from '../api/queries.js';
import { Avatar } from '../components/Badges.js';
import { formatUpdated } from '../lib/format.js';
import styles from './LinksPage.module.css';

/**
 * Ссылки направления: адрес и наименование к нему.
 *
 * Список, а не таблица: колонок всего две, и обе — текст произвольной длины. Строка ведёт туда,
 * куда указывает ссылка, поэтому вся она — якорь, а правка и удаление живут в кнопках справа,
 * которые щелчок по себе до строки не пропускают.
 */
export function LinksPage({ projectId }: { projectId: string }): React.ReactElement {
  const { t } = useTranslation();

  const list = useQuery(linksQuery(projectId));
  const links: ProjectLink[] = useMemo(() => list.data?.links ?? [], [list.data]);

  const create = useLinkCreate(projectId);
  const save = useLinkSave(projectId);
  const remove = useLinkDelete(projectId);

  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  /** Какая строка сейчас правится. Правка идёт по месту: заводить окно ради двух полей незачем. */
  const [editingId, setEditingId] = useState<string | null>(null);

  const submit = useCallback(() => {
    if (url.trim() === '') return;

    create.mutate(
      { url: url.trim(), title: title.trim() },
      {
        onSuccess: () => {
          setUrl('');
          setTitle('');
        },
      },
    );
  }, [create, title, url]);

  // Адрес разбирает сервер: схема дописывает «https://», отвергает всё, кроме http и https.
  const createError = create.error instanceof ApiRequestError ? create.error : null;

  return (
    <div className={styles.page}>
      <div className={styles.sheet}>
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <input
            className={styles.urlInput}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            maxLength={LINK_URL_MAX}
            placeholder={t('links.urlPlaceholder')}
            aria-label={t('links.url')}
            /* Не type="url": браузер потребовал бы схему, а её дописывает сервер. */
            inputMode="url"
            spellCheck={false}
          />
          <input
            className={styles.titleInput}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={LINK_TITLE_MAX}
            placeholder={t('links.titlePlaceholder')}
            aria-label={t('links.title')}
          />
          <button
            type="submit"
            className={styles.addButton}
            disabled={url.trim() === '' || create.isPending}
          >
            {t('links.add')}
          </button>
        </form>

        {createError && (
          <div className={styles.error} role="alert">
            {t(`errors.${createError.code}`)}
          </div>
        )}

        {list.isPending && <div className={styles.empty}>{t('links.loading')}</div>}

        {!list.isPending && links.length === 0 && (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>{t('links.emptyTitle')}</div>
            <p className={styles.emptyBody}>{t('links.emptyBody')}</p>
          </div>
        )}

        <ul className={styles.items}>
          {links.map((link) =>
            link.id === editingId ? (
              <LinkEdit
                link={link}
                key={link.id}
                busy={save.isPending}
                error={save.error instanceof ApiRequestError ? save.error.code : null}
                onCancel={() => {
                  save.reset();
                  setEditingId(null);
                }}
                onSave={(patch) =>
                  save.mutate(
                    { id: link.id, ...patch },
                    { onSuccess: () => setEditingId(null) },
                  )
                }
              />
            ) : (
              <LinkRow
                link={link}
                key={link.id}
                onEdit={() => {
                  save.reset();
                  setEditingId(link.id);
                }}
                onRemove={() => remove.mutate(link.id)}
              />
            ),
          )}
        </ul>
      </div>
    </div>
  );
}

/**
 * Строка списка. Удаление без подтверждения — в отличие от заметки: ссылка не уносит с собой
 * ничего, кроме двух строк текста, и завести её заново — то же движение, что и удалить.
 */
function LinkRow({
  link,
  onEdit,
  onRemove,
}: {
  link: ProjectLink;
  onEdit: () => void;
  onRemove: () => void;
}): React.ReactElement {
  const { t } = useTranslation();

  return (
    <li className={styles.item}>
      <a
        className={styles.link}
        href={link.url}
        target="_blank"
        rel="noreferrer noopener"
        title={link.url}
      >
        <LinkIcon />
        <span className={styles.linkText}>
          <span className={`${styles.linkTitle} truncate`}>{linkLabel(link)}</span>
          <span className={`mono ${styles.linkUrl} truncate`}>{link.url}</span>
        </span>
      </a>

      <span className={styles.meta}>
        {link.updatedBy && <Avatar user={link.updatedBy} size={16} />}
        <span className="mono">{formatUpdated(link.updatedAt, t)}</span>
      </span>

      <span className={styles.actions}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onEdit}
          title={t('links.edit')}
          aria-label={t('links.edit')}
        >
          <PencilIcon />
        </button>
        <button
          type="button"
          className={`${styles.iconButton} ${styles.removeButton}`}
          onClick={onRemove}
          title={t('links.remove')}
          aria-label={t('links.remove')}
        >
          ✕
        </button>
      </span>
    </li>
  );
}

/** Правка по месту: те же два поля, что и в форме добавления, на месте строки. */
function LinkEdit({
  link,
  busy,
  error,
  onSave,
  onCancel,
}: {
  link: ProjectLink;
  busy: boolean;
  error: string | null;
  onSave: (patch: { url: string; title: string }) => void;
  onCancel: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [url, setUrl] = useState(link.url);
  const [title, setTitle] = useState(link.title);

  return (
    <li className={styles.item}>
      <form
        className={styles.editForm}
        onSubmit={(event) => {
          event.preventDefault();
          if (url.trim() !== '') onSave({ url: url.trim(), title: title.trim() });
        }}
      >
        <input
          className={styles.urlInput}
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          maxLength={LINK_URL_MAX}
          aria-label={t('links.url')}
          inputMode="url"
          spellCheck={false}
          autoFocus
        />
        <input
          className={styles.titleInput}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={LINK_TITLE_MAX}
          placeholder={t('links.titlePlaceholder')}
          aria-label={t('links.title')}
        />
        <button type="submit" className={styles.addButton} disabled={url.trim() === '' || busy}>
          {t('links.save')}
        </button>
        <button type="button" className={styles.cancelButton} onClick={onCancel}>
          {t('links.cancel')}
        </button>
      </form>
      {error && (
        <div className={styles.error} role="alert">
          {t(`errors.${error}`)}
        </div>
      )}
    </li>
  );
}

/** Звено цепи — тот же значок, каким ссылку обозначают везде. */
function LinkIcon(): React.ReactElement {
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 16 16"
      width="14"
      height="14"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M6.5 9.5a2.5 2.5 0 0 0 3.6.1l2-2a2.55 2.55 0 0 0-3.6-3.6l-1 1M9.5 6.5a2.5 2.5 0 0 0-3.6-.1l-2 2a2.55 2.55 0 0 0 3.6 3.6l1-1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PencilIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
      <path
        d="M11.2 2.8a1.5 1.5 0 0 1 2.1 2.1L5.6 12.6l-2.8.7.7-2.8z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}
